import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { Drug } from 'src/API';
import { DryEyeMedicalHistoryFormGroupValue } from './dry-eye-medical-history-form.model';
import { SchemaService } from 'src/app/shared/symptoms/services/schema/schema.service';
import { SideEffectFlaggingService } from 'src/app/shared/medication-lookup/side-effect-flagging.service';
import {
  RiskFactor as CanonicalRiskFactor,
  Indicator as CanonicalIndicator,
  RiskLevel,
  modifiableRiskFactors
} from 'src/app/shared/risk-factors-report/risk-factors.model';
import { RiskFactorsService } from 'src/app/shared/risk-factors-report/risk-factors.service';
import {
  ALLERGIC_KEYS,
  buildDetail,
  CLINICAL_CONTEXT_ITEMS,
  DURATION_OBJECT_FACTORS,
  FREE_TEXT_DETAIL_MAP,
  KEY_TO_GROUP,
  OCULAR_SURGERY_KEYWORDS,
  OcularHistoryKeywordEntry,
  SKIP_IF_ZERO_KEYS,
  SUPPLEMENTAL_RISK_FACTORS,
  SYMPTOM_CLUSTER_LABELS,
  SYSTEMIC_KEYS
} from './medical-history-risk-factor-config';
import {
  ALLERGIC_ENVIRONMENTAL_SYMPTOMS_KEY,
  RiskFactor,
  RiskSeverity,
  MedicalHistoryRiskSummary,
  MedicalHistoryRiskSummaryWithDetail,
  ClinicalContextItem,
  ClinicalContextGroups
} from './medical-history-risk-factor.schema';

/**
 * Severity crosswalk — canonical 4-level RiskLevel → medical-history 3-bucket severity.
 * 'none' is dropped upstream; this function only runs on flagged indicators.
 */
function canonicalLevelToSeverity(level: RiskLevel): RiskSeverity | null {
  switch (level) {
    case 'high':   return 'high';
    case 'medium': return 'moderate';
    case 'low':    return 'low';
    default:       return null;
  }
}


const MEDICATION_FACTOR: RiskFactor = {
  key: 'medications',
  label: 'Flagged medications (may cause dry eye)',
  group: 'medications',
  severity: 'high'
};

// Hoisted: neither input mutates at runtime, so the merged map is computed once
// per module load instead of every form-value emission.
const ALL_CANONICAL_FACTORS: Record<string, CanonicalRiskFactor> = {
  ...modifiableRiskFactors,
  ...SUPPLEMENTAL_RISK_FACTORS
};

@Injectable({
  providedIn: 'root'
})
export class MedicalHistoryRiskSummaryService {

  constructor(
    private sideEffectFlaggingService: SideEffectFlaggingService,
    private riskFactorsService: RiskFactorsService,
    private schemaService: SchemaService
  ) {}

  /**
   * Picks the best human-readable label for a canonical risk-factor card.
   * Order: factor `name` (e.g. "Currently experiencing eye dryness")
   *      → matched indicator `contributor` (e.g. "Experiences no eye tearing at all")
   *      → humanized key (camelCase split + capitalized) so a clinician always
   *        sees something readable on the chip.
   * Required because some canonical factors (e.g. `eyeTearingQuantity`,
   * `styeOrChalazion`) have empty `name` and rely on per-indicator contributors.
   */
  private resolveLabel(key: string, factorName: string, contributor: string | undefined): string {
    const trimmedName = (factorName || '').trim();
    if (trimmedName.length > 0) { return trimmedName; }

    const trimmedContributor = (contributor || '').trim();
    if (trimmedContributor.length > 0) {
      return trimmedContributor.charAt(0).toUpperCase() + trimmedContributor.slice(1);
    }

    const spaced = key.replace(/([A-Z])/g, ' $1').replace(/[_-]+/g, ' ').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  /**
   * Evaluates the canonical `modifiableRiskFactors` map against the current form value
   * using `RiskFactorsService.compareValues()`. First matching indicator wins (mirrors
   * the modal's behaviour). Returns cards in the medical-history `RiskFactor` shape so
   * the existing 3-bucket grouping pipeline keeps working.
   */
  private evaluateCanonicalFactors(formValue: DryEyeMedicalHistoryFormGroupValue): RiskFactor[] {
    const hits: RiskFactor[] = [];

    for (const key of Object.keys(ALL_CANONICAL_FACTORS)) {
      const canonicalFactor = ALL_CANONICAL_FACTORS[key];
      const rawValue = formValue[key];

      // Skip-if-zero guard for sliders where 0 means N/A.
      if (SKIP_IF_ZERO_KEYS.has(key) && Number(rawValue) === 0) { continue; }

      const matchedIndicator: CanonicalIndicator | undefined = canonicalFactor.indicators.find(
        indicator => this.riskFactorsService.compareValues(rawValue, indicator.condition, indicator.operator)
      );
      if (!matchedIndicator) { continue; }

      const severity = canonicalLevelToSeverity(matchedIndicator.riskLevel);
      if (!severity) { continue; }

      hits.push({
        key,
        label: this.resolveLabel(key, canonicalFactor.name, matchedIndicator.contributor),
        group: KEY_TO_GROUP[key] || 'medicalConditions',
        severity,
        detail: buildDetail(key, formValue)
      });
    }

    // Duration objects (value shape: { total, unit }) — compareValues can't reach into them.
    for (const key of Object.keys(DURATION_OBJECT_FACTORS)) {
      const durationValue = formValue[key];
      if (!durationValue || typeof durationValue !== 'object') { continue; }
      const totalMonths = Number(durationValue.total);
      if (isNaN(totalMonths) || totalMonths === 0) { continue; }

      const factor = DURATION_OBJECT_FACTORS[key];
      const matched = factor.indicators.find(
        indicator => this.riskFactorsService.compareValues(totalMonths, indicator.condition, indicator.operator)
      );
      if (!matched) { continue; }

      const severity = canonicalLevelToSeverity(matched.riskLevel);
      if (!severity) { continue; }

      hits.push({
        key,
        label: factor.name,
        group: 'symptomDuration',
        severity,
        detail: buildDetail(key, formValue)
      });
    }

    return hits;
  }


  /**
   * Takes the formGroup valueChanges observable and returns a live RiskSummary stream.
   * Non-medication factors are evaluated synchronously on every emission.
   * Medication checks (async HTTP) only re-fire when the medications array reference changes.
   *
   * Contract: callers MUST pass a shared (multicast) Observable. We subscribe to it
   * twice below — once for the medications array (async getSideEffects pipeline) and
   * once via combineLatest for the synchronous canonical/ocular/freeText factors —
   * so a raw cold valueChanges would re-subscribe the form group. The only caller is
   * `buildMedicalHistoryRiskSummaryWithDetail$`, which already wraps with shareReplay(1),
   * so we don't double-buffer here.
   */
  buildMedicalHistoryRiskSummary$(formValue$: Observable<DryEyeMedicalHistoryFormGroupValue>): Observable<MedicalHistoryRiskSummary> {
    const flaggedMedicationFactor$: Observable<RiskFactor | null> = formValue$.pipe(
      map(formValue => [
        ...((formValue.medications as Drug[]) || []),
        ...((formValue.currentOcularMedicationsFreeText as Drug[]) || []),
        ...((formValue.pastOcularMedicationsFreeText as Drug[]) || [])
      ]),
      // The map above always returns a fresh array reference, so the default
      // `===` equality check never matches and would re-fire `getSideEffects`
      // on every keystroke. Compare by length + per-entry id (falling back to
      // name) so the HTTP call only re-fires when the medication list actually
      // changes.
      distinctUntilChanged((previousMedications, currentMedications) => {
        if (previousMedications.length !== currentMedications.length) {
          return false;
        }
        return previousMedications.every((drug, index) => {
          const previousIdentity = drug.id || drug.name;
          const currentIdentity = currentMedications[index].id || currentMedications[index].name;
          return previousIdentity === currentIdentity;
        });
      }),
      switchMap(medications => this.getFlaggedMedicationFactor(medications, MEDICATION_FACTOR))
    );

    return combineLatest([formValue$, flaggedMedicationFactor$]).pipe(
      map(([formValue, flaggedMedicationFactor]) => {
        const canonicalHits = this.evaluateCanonicalFactors(formValue);

        const ocularHistoryFactor          = this.getPastOcularHistoryFactor(formValue);
        // Note: flaggedMedicationFactor (from the structured `medications` Drug[] array)
        // and freeTextMedicationFactor (from the `patientEnteredMedicationsFreeText`
        // text field) are intentionally allowed to BOTH fire — they map to two
        // separate patient-facing fields and render with two separate labels
        // ("Flagged medications (may cause dry eye)" vs "Patient-entered medications"),
        // so showing both cards when both are populated is honest. dedup below
        // collapses only same-label duplicates, not these two.
        const freeTextMedicationFactor     = this.getFreeTextMedicationFactor(formValue);

        const rawFlagged: RiskFactor[] = [
          ...canonicalHits,
          ...(flaggedMedicationFactor   ? [flaggedMedicationFactor]   : []),
          ...(ocularHistoryFactor       ? [ocularHistoryFactor]       : []),
          ...(freeTextMedicationFactor  ? [freeTextMedicationFactor]  : [])
        ];

        const allFlagged = this.aggregateSymptomClusters(rawFlagged, formValue);

        // Dedup BEFORE bucketing so a label that hits in two severities (e.g. once
        // as a canonical high and once as a freeText moderate) does not render
        // twice. Bucket the surviving entries afterward by severity.
        const seenLabels = new Set<string>();
        const dedupedFlagged = allFlagged.filter(medicalHistoryRiskFactor => {
          if (seenLabels.has(medicalHistoryRiskFactor.label)) { return false; }
          seenLabels.add(medicalHistoryRiskFactor.label);
          return true;
        });

        return {
          high:       dedupedFlagged.filter(medicalHistoryRiskFactor => medicalHistoryRiskFactor.severity === 'high'),
          moderate:   dedupedFlagged.filter(medicalHistoryRiskFactor => medicalHistoryRiskFactor.severity === 'moderate'),
          low:        dedupedFlagged.filter(medicalHistoryRiskFactor => medicalHistoryRiskFactor.severity === 'low'),
          hasAnyRisk: dedupedFlagged.length > 0
        };
      })
    );
  }

  /**
   * Collapses individual allergic / systemic symptom cards into single aggregate cards.
   * If ≥1 key in a cluster is truthy, per-field hits for that cluster are removed
   * and one aggregate card is appended. Severity matches the canonical riskLevel
   * for the cluster keys ('low' → 'moderate' bucket).
   */
  private aggregateSymptomClusters(hits: RiskFactor[], formValue: DryEyeMedicalHistoryFormGroupValue): RiskFactor[] {
    const allergicTriggered = ALLERGIC_KEYS.some(k => !!formValue[k]);
    const systemicTriggered = SYSTEMIC_KEYS.some(k => !!formValue[k]);

    const allergicCard: RiskFactor | null = allergicTriggered ? {
      key: ALLERGIC_ENVIRONMENTAL_SYMPTOMS_KEY,
      label: 'Allergic / inflammatory symptoms present',
      group: 'medicalConditions',
      severity: 'moderate',
      detail: ALLERGIC_KEYS.filter(k => !!formValue[k]).map(k => SYMPTOM_CLUSTER_LABELS[k] || k).join(', ') || undefined
    } : null;

    const systemicCard: RiskFactor | null = systemicTriggered ? {
      key: 'systemicSymptoms',
      label: 'Systemic symptoms present',
      group: 'medicalConditions',
      severity: 'moderate',
      detail: SYSTEMIC_KEYS.filter(k => !!formValue[k]).map(k => SYMPTOM_CLUSTER_LABELS[k] || k).join(', ') || undefined
    } : null;

    const suppressed = new Set<string>([
      ...(allergicTriggered ? ALLERGIC_KEYS : []),
      ...(systemicTriggered ? SYSTEMIC_KEYS : [])
    ]);

    return [
      ...hits.filter(rf => !suppressed.has(rf.key)),
      ...(allergicCard ? [allergicCard] : []),
      ...(systemicCard ? [systemicCard] : [])
    ];
  }

  /**
   * Evaluates past ocular history using keyword scanning.
   * HIGH-risk surgeries (LASIK, cataract, trauma, etc.) → HIGH card.
   * Checkbox ticked or MODERATE keywords → MODERATE card.
   * Only LOW or no-match keywords → no card.
   */
  private getPastOcularHistoryFactor(formValue: DryEyeMedicalHistoryFormGroupValue): RiskFactor | null {
    const text: string        = formValue['previousOcularSurgeryFreeText'] || '';
    const checkboxTicked: boolean = formValue['previousOcularSurgery'] === true;
    const textLower           = text.toLowerCase();

    const ocularHistoryBase: Omit<RiskFactor, 'severity'> = {
      key: 'previousOcularSurgeryFreeText',
      label: 'Past ocular history',
      group: 'freeTextPresent',
      detail: text.trim() || undefined
    };

    const hasHighKeyword = OCULAR_SURGERY_KEYWORDS
      .filter(entry => entry.priority === 'HIGH')
      .some(entry => entry.keywords.some(kw => textLower.includes(kw.toLowerCase())));

    if (hasHighKeyword) {
      return { ...ocularHistoryBase, severity: 'high' };
    }

    const hasModerateKeyword = OCULAR_SURGERY_KEYWORDS
      .filter(entry => entry.priority === 'MODERATE')
      .some(entry => entry.keywords.some(kw => textLower.includes(kw.toLowerCase())));

    if (hasModerateKeyword || checkboxTicked) {
      return { ...ocularHistoryBase, severity: 'moderate' };
    }

    return null;
  }

  /**
   * Flags a HIGH risk card when the patient has typed medication names into
   * free-text fields that bypass the structured drug-lookup side-effect check.
   * The clinician should manually review these for dry-eye risk.
   */
  private getFreeTextMedicationFactor(formValue: DryEyeMedicalHistoryFormGroupValue): RiskFactor | null {
    const freeTextFields = ['patientEnteredMedicationsFreeText', 'otherMedications'];
    const parts: string[] = [];
    for (const field of freeTextFields) {
      const val = formValue[field];
      if (typeof val === 'string' && val.trim().length > 0) {
        parts.push(val.trim());
      }
    }
    if (parts.length === 0) { return null; }
    return {
      key: 'patientEnteredMedicationsFreeText',
      label: 'Patient-entered medications',
      group: 'medications',
      severity: 'high',
      detail: parts.join('\n')
    };
  }

  private readonly sideEffectFriendlyNames: Record<string, string> = {
    dryEyes:       'Dry Eyes',
    glaucoma:      'Glaucoma',
    maculopathy:   'Maculopathy',
    opticNeuritis: 'Optic Neuritis/Neuropathy'
  };

  private getFlaggedMedicationFactor(
    medications: Drug[],
    medicationFactor: RiskFactor
  ): Observable<RiskFactor | null> {
    if (!Array.isArray(medications) || medications.length === 0) {
      return of(null);
    }

    const sideEffectChecks$ = medications.map(drug =>
      this.sideEffectFlaggingService.getSideEffects(drug)
    );

    return combineLatest(sideEffectChecks$).pipe(
      map(allSideEffects => {
        const flaggedEntries = medications
          .map((drug, i) => ({ drug, sideEffects: allSideEffects[i] }))
          .filter(entry => entry.sideEffects.dryEyes);

        if (flaggedEntries.length === 0) { return null; }

        // Format: `› ${name}\t${effects}` — TAB separator instead of parens so a
        // drug name containing parentheses (e.g. "Combigan (brimonidine/timolol)")
        // round-trips correctly. Drug names from the lookup never contain tabs.
        const detail = flaggedEntries.map(entry => {
          const effects = Object.entries(entry.sideEffects)
            .filter(([, value]) => value)
            .map(([key]) => this.sideEffectFriendlyNames[key] || key)
            .join(', ');
          return `› ${entry.drug.name}\t${effects}`;
        }).join('\n');

        return { ...medicationFactor, detail };
      })
    );
  }

  /**
   * Enhanced risk summary with grouping, display values, inline notes, and the
   * patient clinical-context panel (occupation, hobbies, Fitzpatrick fields).
   */
  buildMedicalHistoryRiskSummaryWithDetail$(formValue$: Observable<DryEyeMedicalHistoryFormGroupValue>): Observable<MedicalHistoryRiskSummaryWithDetail> {
    const emptySummary: MedicalHistoryRiskSummaryWithDetail = {
      high: [], moderate: [], low: [], hasAnyRisk: false,
      clinicalContext: { primary: [], fitzpatrick: [] }
    };
    // Same shareReplay(1) defensive contract as buildMedicalHistoryRiskSummary$ —
    // formValue$ feeds buildMedicalHistoryRiskSummary$ AND the clinicalContext mapper
    // below, and a non-shared upstream would double-subscribe the form group.
    const sharedFormValue$ = formValue$.pipe(shareReplay(1));
    return combineLatest([
      this.buildMedicalHistoryRiskSummary$(sharedFormValue$),
      sharedFormValue$
    ]).pipe(
      map(([summary, formValue]) => ({
        ...summary,
        clinicalContext: this.buildClinicalContext(formValue)
      })),
      // The medication side-effects HTTP inside buildMedicalHistoryRiskSummary$
      // can delay the first combineLatest emission. startWith ensures consumers
      // (template async pipe, latestViewModel cache, reveal animation) get a
      // sane empty summary immediately and the real one as soon as it arrives.
      startWith(emptySummary),
      catchError(() => of(emptySummary))
    );
  }

  /**
   * Splits populated clinical-context fields into primary and fitzpatrick lists.
   * Items with empty / nullish values are dropped so the panel hides cleanly.
   */
  private buildClinicalContext(formValue: DryEyeMedicalHistoryFormGroupValue): ClinicalContextGroups {
    const primary: ClinicalContextItem[] = [];
    const fitzpatrick: ClinicalContextItem[] = [];

    for (const item of CLINICAL_CONTEXT_ITEMS) {
      const rawValue = formValue ? formValue[item.key] : undefined;
      const displayValue = this.formatClinicalContextValue(rawValue, item.formatter, item.key);
      if (!displayValue) { continue; }

      const enriched: ClinicalContextItem = { ...item, value: rawValue, displayValue };
      if (item.subGroup === 'primary') {
        primary.push(enriched);
      } else {
        fitzpatrick.push(enriched);
      }
    }

    return { primary, fitzpatrick };
  }

  private formatClinicalContextValue(rawValue: any, formatter: string | undefined, key: string): string {
    if (rawValue === null || rawValue === undefined) { return ''; }

    switch (formatter) {
      case 'boolean':
        if (rawValue === true)  { return 'Yes'; }
        if (rawValue === false) { return 'No';  }
        return '';
      case 'radio': {
        // Look up the human label in schema.json (e.g. sleepingPosition +
        // 'sideAndStomach' → 'Side and stomach'). The lookup returns 'N/A'
        // for nullish input — that's filtered out by the rawValue guard at
        // the top, so we only get here when there's a real value to look up.
        // Falls back to the raw value if the schema has no entry for it.
        const friendly = this.schemaService.getFriendlyValueName(rawValue, key);
        const asString = String(friendly || rawValue).trim();
        return asString.length > 0 ? asString : '';
      }
      case 'text':
      default: {
        const asString = String(rawValue).trim();
        return asString.length > 0 ? asString : '';
      }
    }
  }

}
